import { useEffect } from 'react';
import { isElectron } from '../../EnvironmentFlags/EnvironmentFlags';

/**
 * Hardens clipboard and text-selection behaviour while the kiosk Electron build
 * is running, so PII a customer types into an input cannot persist between
 * sessions and stale clipboard content cannot be pasted in:
 *
 * - `copy` / `cut` / `paste` are blocked on the OS clipboard.
 * - `selectstart` is blocked, so a user-initiated selection (mouse drag or a
 *   touchscreen long-press) never forms. With no selection, Chromium's touch
 *   text-selection bar (Copy/Cut) has nothing to attach to and does not appear
 *   — this is what suppresses the long-press menu inside editable fields, where
 *   CSS `user-select: none` is ignored by Blink (see CssBaseLine). Programmatic
 *   selection (`element.select()` / `setSelectionRange()`) does NOT fire
 *   `selectstart`, so the price-field select-all and the on-screen keyboard's
 *   caret handling keep working.
 *
 * PMG-673869 / QA PMR 1671593. Mobile-web builds are intentionally unaffected
 * (gated by `isElectron()`). If a future feature legitimately needs to copy or
 * select something (e.g., a confirmation code), it must `stopPropagation()` on
 * its own handler before this document-level capture-phase listener sees the
 * event.
 *
 * `paste` is safe to block here: the only intentional paste handling is the
 * whitespace sanitizer on the mobile-web email field (WebFormEmailField), which
 * never runs under Electron, so the kiosk has no paste behavior to preserve.
 */
export const useClipboardSuppression = (): void => {
    useEffect(() => {
        if (!isElectron()) {
            return;
        }

        const block = (event: Event): void => {
            event.preventDefault();
        };

        document.addEventListener('copy', block, true);
        document.addEventListener('cut', block, true);
        document.addEventListener('paste', block, true);
        document.addEventListener('selectstart', block, true);

        return () => {
            document.removeEventListener('copy', block, true);
            document.removeEventListener('cut', block, true);
            document.removeEventListener('paste', block, true);
            document.removeEventListener('selectstart', block, true);
        };
    }, []);
};
